import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../../../components/AppIcon';
import { Switch } from '../../../components/ui/Switch';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { InfoTooltip } from '../../../components/ui/Tooltip';

/**
 * One group of the Module Configuration switch table: a band with the group's name, then one dense
 * row per module (or tab group, or the capabilities set), each opening onto its switch rows. Web
 * modules, mobile modules, page-tab groups and the company capabilities all render through this one
 * component (CO-865), so an accessibility or layout change is made once.
 *
 * Which rows are shown and which items are open is decided by `SwitchTable` (search, filters, expand
 * all); this file only draws what it is given.
 *
 * Accessibility contract, which the E2E probes assert:
 *   - every switch has an accessible name (`aria-label`): the row label, or "Enable X module";
 *   - a header is a real `<button aria-expanded aria-controls>` inside a heading, and the module
 *     switch is its SIBLING, never its child, so neither control nests inside the other.
 */

export interface ConfigRowModel {
  /** Storage key (`moduleId:pagePath`). Unique on the page; also the switch's test id suffix. */
  key: string;
  /** Translated label. Also the switch's accessible name. */
  label: string;
  /** The stored path, tab id, capability key or mobile page id, shown in mono beside the label. */
  path?: string;
  checked: boolean;
  disabled: boolean;
  onToggle: (value: boolean) => void;
  /** `locked`: always on, shown read-only. `comingSoon`: the page carries the Soon marker in the sidebar. */
  status?: 'locked' | 'comingSoon';
  /** The value differs from what the server last returned or accepted. */
  changed?: boolean;
  /** The owning module is OFF, so the tenant cannot reach this row whatever its own value. The
   *  stored value is kept and still shown; the switch is unavailable until the module is back ON. */
  hiddenByModule?: boolean;
  /** Names the screens this row governs. Persistent, because an operator scanning the panel should
   *  not have to hover five rows to learn what the section does. */
  description?: string;
  /** The consequence that the screen names alone do not convey. Short sentence, tooltip-sized. */
  hint?: string;
  /** Stored state in words ("On, set", "Off, set", "On, no row"). Absent on locked, demo and staged rows. */
  stateLabel?: string;
  /** The row is a demo surface: its switch mirrors the grant and is read-only. */
  demo?: boolean;
}

export interface ConfigRowGroup {
  id: string;
  /** Translated sub-module title. Omitted for a module's direct pages. */
  heading?: string;
  rows: ConfigRowModel[];
}

export interface ConfigItemModel {
  /** Module id, tab-group id or capabilities id. Used in element ids, so it must hold no whitespace. */
  id: string;
  /** Translated title. */
  title: string;
  /** lucide icon name from the navigation registry, the same one the sidebar shows. */
  icon?: string;
  /** Translated count line beside the title. */
  meta: ReactNode;
  /** The number `meta` states, exposed as `data-count` so a test can read it in any language. */
  count?: number;
  /** Translated "n/m on" for the item's rows. */
  enabledLabel?: string;
  /** The module-level switch differs from what the server last returned or accepted. */
  changed?: boolean;
  /** Module-level switch. Page-tab groups and capabilities have none. */
  toggle?: {
    checked: boolean;
    disabled: boolean;
    onToggle: (value: boolean) => void;
    /** Accessible name, e.g. "Enable Settings module". */
    label: string;
  };
  /** Translated sentence shown at the top of the open panel. */
  intro?: string;
  groups: ConfigRowGroup[];
}

export interface ConfigSectionProps {
  /** Stable section id: `web`, `mobile`, `tabs`, `capabilities`. Prefixes element ids and test ids. */
  id: string;
  /** Translated group name, shown in the band. */
  title: string;
  /** lucide icon name shown beside the group name. */
  icon?: IconName;
  items: ConfigItemModel[];
  /** Ids of the open items of this section. */
  expandedIds: ReadonlySet<string>;
  onToggleExpanded: (itemId: string) => void;
}

/** A dot and a word, never a coloured edge: marks a switch that differs from the saved state. */
export function ChangedTag() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-warning-strong">
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-warning" />
      {t('moduleConfig.changes.changedTag')}
    </span>
  );
}

function ConfigRow({ row }: { row: ConfigRowModel }) {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-h-9 items-center justify-between gap-3 py-1.5 pe-4 ps-10 transition-colors hover:bg-card"
      data-changed={row.changed ? 'true' : undefined}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`min-w-0 truncate text-sm ${row.disabled ? 'text-muted-foreground' : 'text-foreground'}`}>
            {row.label}
          </span>
          {row.path && (
            // A route or a storage key, so it reads left to right in every language.
            <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">
              {row.path}
            </span>
          )}
          {row.changed && <ChangedTag />}
          {row.demo && (
            <StatusBadge variant="indigo" size="sm" className="shrink-0" data-testid={`module-config-demo-badge-${row.key}`}>
              {t('moduleConfig.badge.demo')}
            </StatusBadge>
          )}
          {row.stateLabel && (
            // Same quiet voice as the other row notes: it states what is stored, it is not a status.
            <span className="shrink-0 text-xs text-muted-foreground" data-testid={`module-config-state-${row.key}`}>
              {row.stateLabel}
            </span>
          )}
          {row.hiddenByModule && (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <Icon name="EyeOff" size={12} />
              {t('moduleConfig.hiddenByModule')}
            </span>
          )}
          {row.status === 'locked' && (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <Icon name="Lock" size={12} />
              {t('Always enabled')}
            </span>
          )}
          {row.status === 'comingSoon' && (
            <StatusBadge variant="warning" size="sm" className="shrink-0">
              {t('Coming Soon')}
            </StatusBadge>
          )}
          {row.hint && <InfoTooltip content={row.hint} label={row.label} />}
        </div>
        {row.description && (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{row.description}</p>
        )}
      </div>
      <Switch
        checked={row.checked}
        disabled={row.disabled}
        onCheckedChange={row.onToggle}
        aria-label={row.label}
        data-testid={`module-config-switch-${row.key}`}
      />
    </div>
  );
}

interface ConfigItemProps {
  sectionId: string;
  item: ConfigItemModel;
  expanded: boolean;
  onToggleExpanded: () => void;
}

function ConfigItem({ sectionId, item, expanded, onToggleExpanded }: ConfigItemProps) {
  const headerId = `module-config-header-${sectionId}-${item.id}`;
  const panelId = `module-config-panel-${sectionId}-${item.id}`;

  return (
    <div data-changed={item.changed ? 'true' : undefined}>
      <div className="flex min-h-10 items-stretch">
        <h3 className="min-w-0 flex-1">
          <button
            type="button"
            id={headerId}
            data-testid={headerId}
            data-count={item.count}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={onToggleExpanded}
            className="flex h-full w-full items-center gap-2 px-4 py-2 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {/* Points along the reading direction when closed, down when open. */}
            <Icon
              name="ChevronDown"
              size={16}
              className={`shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none ${
                expanded ? '' : '-rotate-90 rtl:rotate-90'
              }`}
            />
            {item.icon && (
              <Icon name={item.icon as IconName} size={16} className="shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0 truncate text-sm font-medium text-foreground">{item.title}</span>
            {item.changed && <ChangedTag />}
            <span className="hidden min-w-0 truncate text-xs font-normal text-muted-foreground sm:block">
              {item.meta}
            </span>
            {item.enabledLabel && (
              <span className="ms-auto shrink-0 ps-2 text-xs font-normal tabular-nums text-muted-foreground">
                {item.enabledLabel}
              </span>
            )}
          </button>
        </h3>

        {/* Beside the header button, not inside it: toggling the module must not open the panel, and
            a switch nested in a button is unreachable for assistive technology. */}
        {item.toggle && (
          <div className="flex shrink-0 items-center pe-4 ps-2">
            <Switch
              checked={item.toggle.checked}
              disabled={item.toggle.disabled}
              onCheckedChange={item.toggle.onToggle}
              aria-label={item.toggle.label}
              data-testid={`module-config-switch-${item.id}`}
            />
          </div>
        )}
      </div>

      {/* Always in the DOM so `aria-controls` never points at nothing; the rows mount only when open,
          which keeps a closed table at a few dozen switches instead of several hundred. */}
      <div id={panelId} hidden={!expanded} className="border-t border-border bg-muted pb-1">
        {expanded && (
          <>
            {item.intro && (
              <p className="pb-1 pe-4 ps-10 pt-2 text-xs leading-relaxed text-muted-foreground">{item.intro}</p>
            )}
            {item.groups.map(group => {
              if (group.rows.length === 0) return null;
              if (!group.heading) {
                return group.rows.map(row => <ConfigRow key={row.key} row={row} />);
              }
              const groupHeadingId = `${panelId}-${group.id}`;
              return (
                <div key={group.id} role="group" aria-labelledby={groupHeadingId}>
                  <div
                    id={groupHeadingId}
                    className="pb-0.5 pe-4 ps-10 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {group.heading}
                  </div>
                  {group.rows.map(row => (
                    <ConfigRow key={row.key} row={row} />
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

export default function ConfigSection({ id, title, icon, items, expandedIds, onToggleExpanded }: ConfigSectionProps) {
  const headingId = `module-config-section-${id}-heading`;

  return (
    <section aria-labelledby={headingId} data-testid={`module-config-section-${id}`}>
      <div className="flex h-9 items-center gap-2 border-b border-border bg-muted px-4">
        {icon && <Icon name={icon} size={14} className="shrink-0 text-muted-foreground" />}
        <h2 id={headingId} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
      </div>
      <div className="divide-y divide-border">
        {items.map(item => (
          <ConfigItem
            key={item.id}
            sectionId={id}
            item={item}
            expanded={expandedIds.has(item.id)}
            onToggleExpanded={() => onToggleExpanded(item.id)}
          />
        ))}
      </div>
    </section>
  );
}
